/**
 * Mundo Rural - Banner principal / Carrusel
 *
 * Responsabilidades:
 * - Navegación anterior / siguiente.
 * - Autoplay configurable desde Shopify.
 * - Indicadores (dots).
 * - Pausa al interactuar con el banner.
 * - Soporte para swipe en móvil.
 * - Soporte para el Theme Editor de Shopify.
 */

class MRImageBannerSlider {
  constructor(element) {
    this.slider = element;
    this.track = this.slider.querySelector("[data-mr-hero-track]");
    this.slides = Array.from(
      this.slider.querySelectorAll("[data-mr-hero-slide]")
    );

    this.prevButton = this.slider.querySelector("[data-mr-hero-prev]");
    this.nextButton = this.slider.querySelector("[data-mr-hero-next]");
    this.dotsContainer = this.slider.querySelector("[data-mr-hero-dots]");

    this.currentIndex = 0;
    this.autoplayTimer = null;

    this.autoplay = this.slider.dataset.autoplay === "true";

    this.autoplaySpeed =
      Number.parseInt(this.slider.dataset.autoplaySpeed, 10) || 5000;

    this.touchStartX = 0;
    this.touchEndX = 0;

    this.reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    if (!this.track || this.slides.length === 0) {
      return;
    }

    this.init();
  }

  /**
   * Inicializa el carrusel.
   */
  init() {
    this.createDots();
    this.bindEvents();
    this.updateSlider(false);

    if (this.slides.length > 1) {
      this.startAutoplay();
    }
  }

  /**
   * Crea un indicador por cada slide.
   */
  createDots() {
    if (!this.dotsContainer || this.slides.length <= 1) {
      return;
    }

    this.dotsContainer.innerHTML = "";

    this.slides.forEach((_, index) => {
      const dot = document.createElement("button");

      dot.type = "button";
      dot.className = "mr-image-banner-slider__dot";

      dot.setAttribute(
        "aria-label",
        `Ir a la diapositiva ${index + 1}`
      );

      dot.addEventListener("click", () => {
        this.goTo(index);
        this.restartAutoplay();
      });

      this.dotsContainer.appendChild(dot);
    });

    this.dots = Array.from(
      this.dotsContainer.querySelectorAll(
        ".mr-image-banner-slider__dot"
      )
    );
  }

  /**
   * Registra todos los eventos del carrusel.
   */
  bindEvents() {
    this.prevButton?.addEventListener("click", () => {
      this.prev();
      this.restartAutoplay();
    });

    this.nextButton?.addEventListener("click", () => {
      this.next();
      this.restartAutoplay();
    });

    /*
     * Pausa autoplay cuando el usuario está interactuando
     * con el banner.
     */
    this.slider.addEventListener("mouseenter", () => {
      this.stopAutoplay();
    });

    this.slider.addEventListener("mouseleave", () => {
      this.startAutoplay();
    });

    this.slider.addEventListener("focusin", () => {
      this.stopAutoplay();
    });

    this.slider.addEventListener("focusout", () => {
      this.startAutoplay();
    });

    /*
     * Swipe móvil.
     */
    this.slider.addEventListener(
      "touchstart",
      (event) => {
        this.touchStartX = event.changedTouches[0].clientX;
      },
      { passive: true }
    );

    this.slider.addEventListener(
      "touchend",
      (event) => {
        this.touchEndX = event.changedTouches[0].clientX;

        this.handleSwipe();
      },
      { passive: true }
    );
  }

  /**
   * Va directamente a una diapositiva.
   */
  goTo(index) {
    if (this.slides.length <= 1) {
      return;
    }

    /*
     * Permite navegación circular:
     *
     * -1  -> último slide
     * 3   -> primer slide
     */
    if (index < 0) {
      this.currentIndex = this.slides.length - 1;
    } else if (index >= this.slides.length) {
      this.currentIndex = 0;
    } else {
      this.currentIndex = index;
    }

    this.updateSlider();
  }

  /**
   * Siguiente diapositiva.
   */
  next() {
    this.goTo(this.currentIndex + 1);
  }

  /**
   * Diapositiva anterior.
   */
  prev() {
    this.goTo(this.currentIndex - 1);
  }

  /**
   * Actualiza posición del track y estado visual.
   */
  updateSlider(animate = true) {
    if (!this.track) {
      return;
    }

    /*
     * Cada slide ocupa 100%.
     *
     * Slide 1 = 0%
     * Slide 2 = -100%
     * Slide 3 = -200%
     */
    if (!animate || this.reducedMotion) {
      this.track.style.transition = "none";
    } else {
      this.track.style.removeProperty("transition");
    }

    this.track.style.transform =
      `translate3d(-${this.currentIndex * 100}%, 0, 0)`;

    this.updateDots();
    this.updateAccessibility();

    /*
     * Si quitamos momentáneamente la transición durante
     * la inicialización, dejamos que CSS vuelva a controlarla.
     */
    if (!animate && !this.reducedMotion) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.track.style.removeProperty("transition");
        });
      });
    }
  }

  /**
   * Marca el dot correspondiente al slide visible.
   */
  updateDots() {
    if (!this.dots) {
      return;
    }

    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;

      dot.classList.toggle("is-active", isActive);

      dot.setAttribute(
        "aria-current",
        isActive ? "true" : "false"
      );
    });
  }

  /**
   * Evita que elementos pertenecientes a slides ocultos
   * queden expuestos a lectores de pantalla.
   */
  updateAccessibility() {
    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;

      slide.setAttribute(
        "aria-hidden",
        isActive ? "false" : "true"
      );
    });
  }

  /**
   * Inicia el cambio automático.
   */
  startAutoplay() {
    if (
      !this.autoplay ||
      this.slides.length <= 1 ||
      this.reducedMotion ||
      Shopify?.designMode
    ) {
      return;
    }

    this.stopAutoplay();

    this.autoplayTimer = window.setInterval(() => {
      this.next();
    }, this.autoplaySpeed);
  }

  /**
   * Detiene autoplay.
   */
  stopAutoplay() {
    if (!this.autoplayTimer) {
      return;
    }

    window.clearInterval(this.autoplayTimer);

    this.autoplayTimer = null;
  }

  /**
   * Reinicia el contador después de navegación manual.
   */
  restartAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }

  /**
   * Navegación táctil.
   */
  handleSwipe() {
    const distance = this.touchStartX - this.touchEndX;
    const minimumDistance = 50;

    if (Math.abs(distance) < minimumDistance) {
      return;
    }

    if (distance > 0) {
      this.next();
    } else {
      this.prev();
    }

    this.restartAutoplay();
  }

  /**
   * Limpieza utilizada cuando Shopify elimina
   * dinámicamente una sección en el editor.
   */
  destroy() {
    this.stopAutoplay();
  }
}


/* ============================================================
   INICIALIZACIÓN
   ============================================================ */

function initMRImageBannerSliders(root = document) {
  const sliders = [];

  /*
   * Si root ya es el slider, también debemos incluirlo.
   */
  if (root.matches?.("[data-mr-hero-slider]")) {
    sliders.push(root);
  }

  sliders.push(
    ...root.querySelectorAll?.("[data-mr-hero-slider]") ?? []
  );

  sliders.forEach((slider) => {
    /*
     * Evita inicializar dos veces el mismo componente.
     */
    if (slider.mrImageBannerSlider) {
      return;
    }

    slider.mrImageBannerSlider =
      new MRImageBannerSlider(slider);
  });
}


/* ============================================================
   CARGA NORMAL DE LA TIENDA
   ============================================================ */

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    initMRImageBannerSliders();
  });
} else {
  initMRImageBannerSliders();
}


/* ============================================================
   SHOPIFY THEME EDITOR
   ============================================================ */

/*
 * Cuando Shopify vuelve a renderizar una sección
 * desde el editor.
 */
document.addEventListener("shopify:section:load", (event) => {
  initMRImageBannerSliders(event.target);
});


/*
 * Limpieza cuando Shopify descarga una sección.
 */
document.addEventListener("shopify:section:unload", (event) => {
  const slider =
    event.target.querySelector("[data-mr-hero-slider]") ||
    (event.target.matches?.("[data-mr-hero-slider]")
      ? event.target
      : null);

  slider?.mrImageBannerSlider?.destroy();
});


/*
 * Cuando seleccionamos una diapositiva desde
 * el editor de Shopify, mostramos automáticamente
 * ese slide.
 */
document.addEventListener("shopify:block:select", (event) => {
  const selectedSlide = event.target.closest(
    "[data-mr-hero-slide]"
  );

  if (!selectedSlide) {
    return;
  }

  const sliderElement = selectedSlide.closest(
    "[data-mr-hero-slider]"
  );

  const slider = sliderElement?.mrImageBannerSlider;

  if (!slider) {
    return;
  }

  const index = slider.slides.indexOf(selectedSlide);

  if (index === -1) {
    return;
  }

  slider.stopAutoplay();
  slider.goTo(index);
});
